/**
 * Paneel voor de deterministische kleur/dim-overlay (conditional formatting).
 *
 * De modelleur bouwt regels (conditie → effect) die een gewoon diagram
 * inkleuren of dimmen op basis van type/subtype, stereotype, laag en
 * attribuut-waarden. Niet-destructief: "Wis overlay" (of de master-schakelaar
 * uit) zet het diagram terug op de standaard-notatiekleuren. De regelset leeft
 * op de diagram-viewState; de engine (`evaluateOverlay`) past 'm deterministisch
 * toe in `Canvas`/`ConceptNode`.
 *
 * Dit is de handmatige regel-editor; een latere LLM-laag schrijft dezelfde
 * regel-JSON vanuit natuurlijke taal (het toepassen blijft deterministisch).
 */
import type {
  AttributeSchema,
  OverlayAttributeClause,
  OverlayComparator,
  OverlayCondition,
  OverlayEffect,
  OverlayRelationClause,
  OverlayRule,
  OverlayRuleSet,
} from "@ae/domain";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";
import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { type OverlayKnownIds, sanitizeOverlayRules } from "./overlayAi";
import { readOverlayRules } from "./overlayConfig";

const COMPARATORS: OverlayComparator[] = [
  "eq",
  "neq",
  "contains",
  "in",
  "gt",
  "gte",
  "lt",
  "lte",
  "exists",
  "not_exists",
];

const DEFAULT_FILL = "#fca5a5"; // zacht rood — meest voorkomende overlay-kleur

interface RuleFormState {
  label: string;
  typeId: string;
  stereotype: string;
  layer: string;
  attrName: string;
  comparator: OverlayComparator;
  value: string;
  /** Child-voorwaarde: concept heeft een child die naar dit AttributeType verwijst. */
  childAttrType: string;
  /** Relatie-voorwaarde: de naam van het object aan de andere kant. */
  relatedName: string;
  /**
   * De relatie-clausule zoals de regel die had (bv. van de AI of MCP, met
   * relatietypen). Bewerken houdt die vast; het formulier toont alleen de naam.
   */
  relation?: OverlayRelationClause | undefined;
  useFill: boolean;
  fill: string;
  dim: boolean;
}

function emptyForm(): RuleFormState {
  return {
    label: "",
    typeId: "",
    stereotype: "",
    layer: "",
    attrName: "",
    comparator: "eq",
    value: "",
    childAttrType: "",
    relatedName: "",
    useFill: true,
    fill: DEFAULT_FILL,
    dim: false,
  };
}

function comparatorNeedsValue(cmp: OverlayComparator): boolean {
  return cmp !== "exists" && cmp !== "not_exists";
}

/**
 * Verzamel de kandidaat-attributen voor de attribuut-dropdown, gededupliceerd
 * op interne naam. Bron: het gekozen type (`getElementType` mergt subtype-
 * `added_attributes` al mee) + het gekozen stereotype. Niets gekozen → unie
 * over alle actieve types + alle stereotypes, zodat een attribuut-only-regel
 * ook zonder type/stereotype te maken is.
 */
/**
 * De eigen naam van het object, als attribuut `name` aangeboden. Elk object
 * heeft er een, dus een regel als "kleur Partij rood" kan altijd. De engine
 * (`evaluateOverlay`) vergelijkt in beide talen.
 */
const NAME_ATTRIBUTE: AttributeSchema = {
  name: "name",
  label: { nl: "Naam", en: "Name" },
  type: "string",
};

function collectAttributes(typeId: string, stereotype: string): AttributeSchema[] {
  const reg = getRegistry();
  const out = new Map<string, AttributeSchema>();
  const add = (list?: AttributeSchema[]) => {
    for (const a of list ?? []) if (!out.has(a.name)) out.set(a.name, a);
  };
  if (typeId) add(reg.getElementType(typeId)?.attributes);
  if (stereotype) add(reg.getStereotype(stereotype)?.attributes);
  if (!typeId && !stereotype) {
    for (const et of reg.listActiveElementTypes()) add(et.attributes);
    for (const st of reg.listStereotypes()) add(st.attributes);
  }
  // Een echt attribuut `name` gaat voor; de engine doet hetzelfde.
  return out.has("name") ? [...out.values()] : [NAME_ATTRIBUTE, ...out.values()];
}

/** Weergavelabel van een enum-optie: `option_labels` indien aanwezig, anders de ruwe waarde. */
function enumLabel(attr: AttributeSchema | undefined, option: string, lang: string): string {
  const label = attr?.option_labels?.[option];
  return (label && resolveBilingual(label, lang)) || option;
}

export function OverlayRulesPanel({
  workspaceId,
  branchId,
}: {
  workspaceId: string | null;
  branchId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("nl") ? "nl" : "en";
  const open = useUi((s) => s.overlayPanelOpen);
  const setOpen = useUi((s) => s.setOverlayPanelOpen);
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const setOverlayRules = useWorkspaceData((s) => s.setOverlayRules);
  const sessionWs = useSession((s) => s.workspaceId);
  // Alleen kijken: de regels staan op het diagram — bewerken is schrijven.
  const readOnly = useSession((s) => s.readOnly);
  const wsId = workspaceId ?? sessionWs;

  const [editing, setEditing] = useState<{ id: string | null; form: RuleFormState } | null>(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState<{ kind: "error" | "info"; text: string } | null>(null);

  const { ruleset, diagramId, isCustomRender } = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const diagram = activeDiagramId ? store.getDiagram(activeDiagramId) : null;
    const rs = readOverlayRules(diagram?.viewState) ?? {
      enabled: false,
      strategy: "first-match" as const,
      rules: [],
    };
    const layoutMode = diagram?.viewKind
      ? getRegistry().getViewKind(diagram.viewKind)?.layout_mode
      : undefined;
    return {
      ruleset: rs,
      diagramId: diagram?.id ?? null,
      // Heat-map / roadmap / glossary hebben een eigen render-pad — de overlay
      // (die op ConceptNode werkt) raakt die views niet.
      isCustomRender: Boolean(layoutMode && layoutMode !== "free"),
    };
  }, [activeDiagramId, revision]);

  const elementTypes = useMemo(() => {
    const reg = getRegistry();
    return reg
      .listActiveElementTypes()
      .map((et) => ({ id: et.id, label: resolveBilingual(et.label, lang), layer: et.layer }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [lang]);

  const stereotypes = useMemo(() => {
    const reg = getRegistry();
    return reg
      .listStereotypes()
      .map((st) => ({ id: st.id, label: resolveBilingual(st.label, lang) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [lang]);

  const relationTypes = useMemo(
    () =>
      getRegistry()
        .listActiveRelationTypes()
        .map((rt) => ({ id: rt.id, label: resolveBilingual(rt.label, lang) })),
    [lang],
  );

  const layers = useMemo(() => {
    const set = new Set(elementTypes.map((et) => et.layer));
    return [...set].sort();
  }, [elementTypes]);

  // Glossary-attribuuttypen (`sharp.AttributeType`) uit de workspace — voor de
  // child-voorwaarde "heeft een child dat naar dit attribuuttype verwijst".
  const attributeTypes = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    return store
      .listConcepts()
      .filter((c) => c.typeId === "sharp.AttributeType" && !c.tombstoneInBranch)
      .map((c) => ({ id: c.id, name: resolveBilingual(c.name, lang) || c.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [revision, lang]);
  const attrTypeName = (id: string) => attributeTypes.find((a) => a.id === id)?.name ?? id;

  if (!open || readOnly) return null;

  const persist = (next: OverlayRuleSet) => {
    if (!wsId || !branchId || !diagramId) return;
    if (useSession.getState().readOnly) return;
    setOverlayRules(wsId, branchId, diagramId, next);
  };

  const setEnabled = (enabled: boolean) => persist({ ...ruleset, enabled });
  const setStrategy = (strategy: "first-match" | "last-match") => persist({ ...ruleset, strategy });

  const clearAll = () =>
    persist({ enabled: false, strategy: ruleset.strategy ?? "first-match", rules: [] });

  const toggleRule = (id: string, enabled: boolean) =>
    persist({
      ...ruleset,
      rules: ruleset.rules.map((r) => (r.id === id ? { ...r, enabled } : r)),
    });

  const deleteRule = (id: string) =>
    persist({ ...ruleset, rules: ruleset.rules.filter((r) => r.id !== id) });

  // Verplaats een regel omhoog/omlaag — volgorde bepaalt de precedentie
  // (eerste- of laatste-wint) wanneer meerdere regels hetzelfde element raken.
  const moveRule = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= ruleset.rules.length) return;
    const rules = [...ruleset.rules];
    const moved = rules[index];
    if (!moved) return;
    rules.splice(index, 1);
    rules.splice(target, 0, moved);
    persist({ ...ruleset, rules });
  };

  const saveRule = () => {
    if (!editing) return;
    const rule = buildRule(editing.form, editing.id, lang);
    if (!rule) return;
    const exists = ruleset.rules.some((r) => r.id === rule.id);
    const rules = exists
      ? ruleset.rules.map((r) => (r.id === rule.id ? rule : r))
      : [...ruleset.rules, rule];
    // Eerste regel toevoegen zet de overlay meteen aan.
    persist({ ...ruleset, enabled: ruleset.enabled || !exists, rules });
    setEditing(null);
  };

  const startEdit = (rule: OverlayRule) => setEditing({ id: rule.id, form: ruleToForm(rule) });

  const generateFromAi = async () => {
    const p = aiPrompt.trim();
    if (!p || aiBusy || !diagramId) return;
    setAiBusy(true);
    setAiMsg(null);
    try {
      const allAttrs = collectAttributes("", "");
      const context = {
        layers,
        types: elementTypes.map((et) => ({ id: et.id, label: et.label })),
        stereotypes,
        attributes: allAttrs.map((a) => ({
          name: a.name,
          label: resolveBilingual(a.label, lang) || a.name,
          type: a.type,
          ...(a.options?.length ? { options: a.options } : {}),
        })),
        attributeTypes,
        relationTypes: relationTypes.map((rt) => ({ id: rt.id, label: rt.label })),
      };
      const known: OverlayKnownIds = {
        types: new Set(elementTypes.map((et) => et.id)),
        stereotypes: new Set(stereotypes.map((s) => s.id)),
        layers: new Set(layers),
        attributes: new Set(allAttrs.map((a) => a.name)),
        attributeTypes: new Set(attributeTypes.map((a) => a.id)),
        relationTypes: new Set(relationTypes.map((rt) => rt.id)),
      };
      const res = await api.suggestOverlayRules({ prompt: p, context });
      const clean = sanitizeOverlayRules(res.rules ?? [], known);
      if (clean.length === 0) {
        setAiMsg({ kind: "error", text: t("overlay.aiNoRules") });
        return;
      }
      persist({ ...ruleset, enabled: true, rules: [...ruleset.rules, ...clean] });
      setAiPrompt("");
      setAiMsg({ kind: "info", text: t("overlay.aiAdded", { count: clean.length }) });
    } catch (e) {
      const text = e instanceof ApiError ? (e.body?.detail ?? e.message) : t("overlay.aiFailed");
      setAiMsg({ kind: "error", text });
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="ledger absolute right-2 top-14 z-40 flex max-h-[80vh] w-96 flex-col overflow-hidden rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl">
      <div className="flex items-center justify-between border-(--ld-border) border-b px-3 py-2">
        <div className="font-semibold text-(--ld-text) text-sm">{t("overlay.title")}</div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded px-1.5 text-(--ld-muted) hover:bg-(--ld-raised)"
          title={t("overlay.close")}
        >
          ✕
        </button>
      </div>

      {!diagramId ? (
        <div className="p-4 text-(--ld-muted) text-sm">{t("overlay.noDiagram")}</div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {isCustomRender && (
            <div className="border-transparent border-b bg-(--ld-notice-bg) px-3 py-2 text-(--ld-notice-ink) text-xs">
              {t("overlay.customRenderNote")}
            </div>
          )}

          {/* Master-controls */}
          <div className="flex items-center justify-between gap-2 border-(--ld-rule) border-b px-3 py-2">
            <label className="flex items-center gap-2 text-(--ld-text) text-sm">
              <input
                type="checkbox"
                checked={ruleset.enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              {t("overlay.enable")}
            </label>
            <label className="flex items-center gap-1 text-(--ld-muted) text-xs">
              {t("overlay.strategy")}
              <select
                value={ruleset.strategy ?? "first-match"}
                onChange={(e) => setStrategy(e.target.value as "first-match" | "last-match")}
                className="rounded border border-(--ld-control) px-1 py-0.5 text-xs"
              >
                <option value="first-match">{t("overlay.firstMatch")}</option>
                <option value="last-match">{t("overlay.lastMatch")}</option>
              </select>
            </label>
          </div>

          {/* AI-auteur — natuurlijke taal → regels (grounded + gesaneerd) */}
          <div className="flex flex-col gap-1.5 border-(--ld-rule) border-b bg-(--ld-raised) px-3 py-2">
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={t("overlay.aiPlaceholder")}
              rows={2}
              className="resize-none rounded border border-(--ld-control) px-2 py-1 text-sm"
            />
            <div className="flex items-center justify-between gap-2">
              <span
                className={`min-w-0 flex-1 truncate text-xs ${
                  aiMsg?.kind === "error" ? "text-(--ld-negative)" : "text-(--ld-muted)"
                }`}
              >
                {aiMsg?.text ?? ""}
              </span>
              <button
                type="button"
                onClick={generateFromAi}
                disabled={aiBusy || !aiPrompt.trim()}
                className="shrink-0 rounded bg-(--ld-primary) px-3 py-1 text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:opacity-40"
              >
                {aiBusy ? t("overlay.aiBusy") : `✨ ${t("overlay.aiGenerate")}`}
              </button>
            </div>
          </div>

          {/* Regel-lijst */}
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
            {ruleset.rules.length === 0 && !editing && (
              <div className="py-6 text-center text-(--ld-muted) text-sm">
                {t("overlay.noRules")}
              </div>
            )}
            <ul className="flex flex-col gap-1">
              {ruleset.rules.map((rule, idx) => (
                <li
                  key={rule.id}
                  className="flex items-center gap-2 rounded border border-(--ld-border) px-2 py-1.5"
                >
                  <div className="flex flex-col leading-none">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => moveRule(idx, -1)}
                      title={t("overlay.moveUp")}
                      className="px-0.5 text-xs text-(--ld-muted) hover:text-(--ld-text) disabled:opacity-25"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      disabled={idx === ruleset.rules.length - 1}
                      onClick={() => moveRule(idx, 1)}
                      title={t("overlay.moveDown")}
                      className="px-0.5 text-xs text-(--ld-muted) hover:text-(--ld-text) disabled:opacity-25"
                    >
                      ▼
                    </button>
                  </div>
                  <input
                    type="checkbox"
                    checked={rule.enabled !== false}
                    onChange={(e) => toggleRule(rule.id, e.target.checked)}
                    title={t("overlay.ruleEnabled")}
                  />
                  <span
                    className="inline-block h-4 w-4 shrink-0 rounded border border-(--ld-control)"
                    style={{
                      backgroundColor: rule.effect.fill ?? "transparent",
                      opacity: rule.effect.dim ? 0.4 : 1,
                    }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-(--ld-text) text-sm">
                      {resolveBilingual(rule.label ?? {}, lang) || t("overlay.unnamedRule")}
                    </div>
                    <div className="truncate text-(--ld-muted) text-xs">
                      {summarizeCondition(rule.condition, lang, (k) => t(k), attrTypeName)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => startEdit(rule)}
                    className="rounded px-1.5 text-(--ld-muted) text-xs hover:bg-(--ld-raised)"
                  >
                    {t("overlay.edit")}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRule(rule.id)}
                    className="rounded px-1.5 text-(--ld-negative) text-xs hover:bg-(--ld-negative-bg)"
                  >
                    {t("overlay.delete")}
                  </button>
                </li>
              ))}
            </ul>

            {editing ? (
              <RuleEditor
                form={editing.form}
                setForm={(form) => setEditing({ id: editing.id, form })}
                onSave={saveRule}
                onCancel={() => setEditing(null)}
                elementTypes={elementTypes}
                stereotypes={stereotypes}
                layers={layers}
                attributeTypes={attributeTypes}
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditing({ id: null, form: emptyForm() })}
                className="mt-2 w-full rounded border border-(--ld-control) border-dashed py-1.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised)"
              >
                + {t("overlay.addRule")}
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-(--ld-border) border-t px-3 py-2">
            <button
              type="button"
              onClick={clearAll}
              disabled={ruleset.rules.length === 0}
              className="rounded px-2 py-1 text-(--ld-muted) text-sm hover:bg-(--ld-raised) disabled:opacity-40"
            >
              {t("overlay.clear")}
            </button>
            <span className="text-(--ld-muted) text-xs">
              {t("overlay.ruleCount", { count: ruleset.rules.length })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

interface TypeOpt {
  id: string;
  label: string;
  layer: string;
}

function RuleEditor({
  form,
  setForm,
  onSave,
  onCancel,
  elementTypes,
  stereotypes,
  layers,
  attributeTypes,
}: {
  form: RuleFormState;
  setForm: (f: RuleFormState) => void;
  onSave: () => void;
  onCancel: () => void;
  elementTypes: TypeOpt[];
  stereotypes: { id: string; label: string }[];
  layers: string[];
  attributeTypes: { id: string; name: string }[];
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("nl") ? "nl" : "en";
  const patch = (p: Partial<RuleFormState>) => setForm({ ...form, ...p });
  const canSave = form.useFill || form.dim;

  // Beschikbare attributen voor de dropdown — afgeleid uit het gekozen type
  // (subtype-attrs zitten al in `getElementType`) en/of stereotype; niets
  // gekozen → unie over alle types + stereotypes (dedup op naam). Zo hoeft de
  // modelleur de interne attribuutsleutel niet te kennen.
  const attrs = useMemo(
    () => collectAttributes(form.typeId, form.stereotype),
    [form.typeId, form.stereotype],
  );
  const attrOptions = useMemo(() => {
    const list = attrs.map((a) => ({
      name: a.name,
      label: resolveBilingual(a.label, lang) || a.name,
    }));
    // Behoud een reeds gekozen sleutel die niet (meer) in de lijst staat
    // (bv. door een LLM geschreven regel of na wisselen van type).
    if (form.attrName && !list.some((o) => o.name === form.attrName)) {
      list.push({ name: form.attrName, label: form.attrName });
    }
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [attrs, form.attrName, lang]);
  const selectedAttr = attrs.find((a) => a.name === form.attrName);
  const enumValues = selectedAttr?.options ?? [];
  // Pack-attribuuttypes dragen een optioneel-suffix (`date?`, `integer?`, …);
  // strip 'm om de juiste invoer-widget te kiezen.
  const attrKind = (selectedAttr?.type ?? "").replace(/\?$/, "");
  const isDateAttr = attrKind === "date";
  const isNumberAttr = attrKind === "integer" || attrKind === "int";

  return (
    <div className="mt-2 flex flex-col gap-2 rounded border border-(--ld-control) bg-(--ld-raised) p-2">
      <input
        type="text"
        value={form.label}
        onChange={(e) => patch({ label: e.target.value })}
        placeholder={t("overlay.labelPlaceholder")}
        className="rounded border border-(--ld-control) px-2 py-1 text-sm"
      />

      <div className="text-(--ld-muted) text-xs">{t("overlay.condition")}</div>

      <select
        value={form.typeId}
        onChange={(e) => patch({ typeId: e.target.value })}
        className="rounded border border-(--ld-control) px-2 py-1 text-sm"
      >
        <option value="">{t("overlay.anyType")}</option>
        {elementTypes.map((et) => (
          <option key={et.id} value={et.id}>
            {et.label}
          </option>
        ))}
      </select>

      <select
        value={form.stereotype}
        onChange={(e) => patch({ stereotype: e.target.value })}
        className="rounded border border-(--ld-control) px-2 py-1 text-sm"
      >
        <option value="">{t("overlay.anyStereotype")}</option>
        {stereotypes.map((st) => (
          <option key={st.id} value={st.id}>
            {st.label}
          </option>
        ))}
      </select>

      <select
        value={form.layer}
        onChange={(e) => patch({ layer: e.target.value })}
        className="rounded border border-(--ld-control) px-2 py-1 text-sm"
      >
        <option value="">{t("overlay.anyLayer")}</option>
        {layers.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>

      {/* Attribuut-clausule — attribuutnaam via dropdown (label getoond, sleutel
          opgeslagen); enum-attributen krijgen ook een waarde-dropdown. */}
      <div className="flex flex-col gap-1 rounded border border-(--ld-border) p-1.5">
        <select
          value={form.attrName}
          onChange={(e) => patch({ attrName: e.target.value, value: "" })}
          className="rounded border border-(--ld-control) px-2 py-1 text-sm"
        >
          <option value="">{t("overlay.attrNone")}</option>
          {attrOptions.map((o) => (
            <option key={o.name} value={o.name}>
              {o.label}
            </option>
          ))}
        </select>
        {form.attrName.trim() && (
          <div className="flex gap-1">
            <select
              value={form.comparator}
              onChange={(e) => patch({ comparator: e.target.value as OverlayComparator })}
              className="rounded border border-(--ld-control) px-1 py-1 text-sm"
            >
              {COMPARATORS.map((c) => (
                <option key={c} value={c}>
                  {t(`overlay.cmp.${c}`)}
                </option>
              ))}
            </select>
            {comparatorNeedsValue(form.comparator) &&
              (enumValues.length > 0 && form.comparator !== "in" ? (
                <select
                  value={form.value}
                  onChange={(e) => patch({ value: e.target.value })}
                  className="min-w-0 flex-1 rounded border border-(--ld-control) px-1 py-1 text-sm"
                >
                  <option value="">{t("overlay.pickValue")}</option>
                  {enumValues.map((opt) => (
                    <option key={opt} value={opt}>
                      {enumLabel(selectedAttr, opt, lang)}
                    </option>
                  ))}
                </select>
              ) : isDateAttr && form.comparator !== "in" ? (
                <input
                  type="date"
                  value={form.value}
                  onChange={(e) => patch({ value: e.target.value })}
                  className="min-w-0 flex-1 rounded border border-(--ld-control) px-2 py-1 text-sm"
                />
              ) : isNumberAttr && form.comparator !== "in" ? (
                <input
                  type="number"
                  value={form.value}
                  onChange={(e) => patch({ value: e.target.value })}
                  placeholder={t("overlay.valuePlaceholder")}
                  className="min-w-0 flex-1 rounded border border-(--ld-control) px-2 py-1 text-sm"
                />
              ) : (
                <input
                  type="text"
                  value={form.value}
                  onChange={(e) => patch({ value: e.target.value })}
                  placeholder={
                    form.comparator === "in"
                      ? t("overlay.valueListPlaceholder")
                      : t("overlay.valuePlaceholder")
                  }
                  className="min-w-0 flex-1 rounded border border-(--ld-control) px-2 py-1 text-sm"
                />
              ))}
          </div>
        )}
      </div>

      <label className="flex flex-col gap-1 rounded border border-(--ld-border) p-1.5">
        <span className="text-(--ld-muted) text-xs">{t("overlay.relatedTo")}</span>
        <input
          type="text"
          value={form.relatedName}
          onChange={(e) => patch({ relatedName: e.target.value })}
          placeholder={t("overlay.relatedToPlaceholder")}
          className="rounded border border-(--ld-control) px-2 py-1 text-sm"
        />
      </label>

      {/* Child-voorwaarde (Sharp): matcht wanneer het concept een Attribuut/
          Identifier-child heeft die naar dit attribuuttype verwijst. */}
      {attributeTypes.length > 0 && (
        <div className="flex flex-col gap-1 rounded border border-(--ld-border) p-1.5">
          <span className="text-(--ld-muted) text-xs">{t("overlay.hasAttrType")}</span>
          <select
            value={form.childAttrType}
            onChange={(e) => patch({ childAttrType: e.target.value })}
            className="rounded border border-(--ld-control) px-2 py-1 text-sm"
          >
            <option value="">{t("overlay.childNone")}</option>
            {attributeTypes.map((at) => (
              <option key={at.id} value={at.id}>
                {at.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="text-(--ld-muted) text-xs">{t("overlay.effect")}</div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-(--ld-text) text-sm">
          <input
            type="checkbox"
            checked={form.useFill}
            onChange={(e) => patch({ useFill: e.target.checked })}
          />
          {t("overlay.fill")}
        </label>
        <input
          type="color"
          value={form.fill}
          disabled={!form.useFill}
          onChange={(e) => patch({ fill: e.target.value })}
          className="h-7 w-10 rounded border border-(--ld-control) disabled:opacity-40"
        />
        <label className="flex items-center gap-1.5 text-(--ld-text) text-sm">
          <input
            type="checkbox"
            checked={form.dim}
            onChange={(e) => patch({ dim: e.target.checked })}
          />
          {t("overlay.dim")}
        </label>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-(--ld-muted) text-sm hover:bg-(--ld-raised)"
        >
          {t("overlay.cancel")}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          className="rounded bg-(--ld-primary) px-3 py-1 text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:opacity-40"
        >
          {t("overlay.save")}
        </button>
      </div>
    </div>
  );
}

function ruleToForm(rule: OverlayRule): RuleFormState {
  const cond = rule.condition;
  const clause = cond.attributes?.[0];
  const childRef = cond.child?.attributes?.find((a) => a.attribute === "attribute_type_ref")?.value;
  return {
    label: rule.label?.nl ?? rule.label?.en ?? "",
    typeId: cond.typeIds?.[0] ?? "",
    stereotype: cond.stereotypes?.[0] ?? "",
    layer: cond.layers?.[0] ?? "",
    attrName: clause?.attribute ?? "",
    comparator: clause?.comparator ?? "eq",
    value: clause
      ? Array.isArray(clause.value)
        ? clause.value.join(", ")
        : (clause.value?.toString() ?? "")
      : "",
    childAttrType: typeof childRef === "string" ? childRef : "",
    relatedName: cond.relation?.otherName ?? "",
    relation: cond.relation,
    useFill: rule.effect.fill !== undefined,
    fill: rule.effect.fill ?? DEFAULT_FILL,
    dim: rule.effect.dim === true,
  };
}

function buildRule(form: RuleFormState, id: string | null, lang: string): OverlayRule | null {
  const condition: OverlayCondition = {};
  if (form.typeId) condition.typeIds = [form.typeId];
  if (form.stereotype) condition.stereotypes = [form.stereotype];
  if (form.layer) condition.layers = [form.layer];
  if (form.attrName.trim()) {
    const clause: OverlayAttributeClause = {
      attribute: form.attrName.trim(),
      comparator: form.comparator,
    };
    if (comparatorNeedsValue(form.comparator)) {
      clause.value =
        form.comparator === "in"
          ? form.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : form.value;
    }
    condition.attributes = [clause];
  }
  const relation: OverlayRelationClause = { ...form.relation };
  if (form.relatedName.trim()) relation.otherName = form.relatedName.trim();
  else delete relation.otherName;
  if (relation.relationTypeIds?.length || relation.otherTypeIds?.length || relation.otherName) {
    condition.relation = relation;
  }
  if (form.childAttrType) {
    condition.child = {
      attributes: [
        { attribute: "attribute_type_ref", comparator: "eq", value: form.childAttrType },
      ],
    };
  }

  const effect: OverlayEffect = {};
  if (form.useFill) effect.fill = form.fill;
  if (form.dim) effect.dim = true;
  if (effect.fill === undefined && !effect.dim) return null;

  const rule: OverlayRule = {
    id: id ?? crypto.randomUUID(),
    condition,
    effect,
  };
  if (form.label.trim()) rule.label = { [lang]: form.label.trim() };
  return rule;
}

function summarizeCondition(
  cond: OverlayCondition,
  lang: string,
  tr: (k: string) => string,
  attrTypeName: (id: string) => string,
): string {
  const parts: string[] = [];
  const tid = cond.typeIds?.[0];
  if (tid) {
    const label = resolveBilingual(getRegistry().getElementType(tid)?.label ?? {}, lang);
    parts.push(label || tid);
  }
  const st = cond.stereotypes?.[0];
  if (st) parts.push(`«${st.split(".").pop()}»`);
  const ly = cond.layers?.[0];
  if (ly) parts.push(ly);
  const c = cond.attributes?.[0];
  if (c) {
    const val = Array.isArray(c.value) ? c.value.join("|") : (c.value ?? "");
    parts.push(`${c.attribute} ${tr(`overlay.cmp.${c.comparator}`)} ${val}`.trim());
  }
  const rel = cond.relation;
  if (rel) {
    const other =
      rel.otherName ??
      rel.otherTypeIds
        ?.map((id) => resolveBilingual(getRegistry().getElementType(id)?.label ?? {}, lang) || id)
        .join("|") ??
      rel.relationTypeIds?.join("|") ??
      "";
    parts.push(`${tr("overlay.relatedToShort")} ${other}`.trim());
  }
  const childRef = cond.child?.attributes?.find((a) => a.attribute === "attribute_type_ref")?.value;
  if (typeof childRef === "string") {
    parts.push(`${tr("overlay.hasAttrType")} ${attrTypeName(childRef)}`);
  }
  return parts.join(" · ") || tr("overlay.anyMatch");
}
